import React, { useEffect, useState } from 'react';
import { Calendar, Check } from 'iconoir-react';
import toast from 'react-hot-toast';
import { eventService, VALID_GOALS, type EventItem } from '../../services/eventService';
import Spinner from '../common/Spinner';

const goalLabel = (slug: string) =>
  VALID_GOALS.find((g) => g.value === slug)?.label || slug;

/**
 * Multi-event: the owner-side home for per-event goals. A user in more than one
 * event can see, for each event they belong to, the objective they set there
 * (or "Same as your profile") and change it. The per-event goal is otherwise
 * only capturable once, at the join prompt; this makes it viewable and editable
 * afterwards. Lists ONLY the user's own events, so it does not reintroduce
 * event browsing (the B2B2C single-event isolation decision stands).
 *
 * Gated to >1 event: with a single event the objective is just the profile
 * objective (already shown above), and the matcher's per-event overlay only
 * applies to multi-event users, so there would be nothing meaningful to edit.
 */
const MyEventsCard = () => {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [savingId, setSavingId] = useState<string | null>(null);

  const load = () => {
    eventService
      .myEvents()
      .then((evs) => setEvents(Array.isArray(evs) ? evs : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className='bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 flex justify-center'>
        <Spinner size='md' />
      </div>
    );
  }

  // Only relevant for multi-event users (see component docstring).
  if (events.length <= 1) return null;

  const toggleGoal = (goal: string) => {
    setSelectedGoals((prev) => {
      if (prev.includes(goal)) return prev.filter((g) => g !== goal);
      if (prev.length >= 2) return prev;
      return [...prev, goal];
    });
  };

  const startEdit = (ev: EventItem) => {
    setSelectedGoals(ev.my_goals ?? []);
    setEditingId(ev.id);
  };

  const save = async (ev: EventItem) => {
    setSavingId(ev.id);
    try {
      await eventService.setMyGoals(ev.id, selectedGoals);
      // Saving chips supersedes any earlier interview for this event, server
      // side. Clear the intent locally to match, or the card keeps showing the
      // superseded objective next to the goals that replaced it.
      setEvents((prev) =>
        prev.map((e) =>
          e.id === ev.id
            ? { ...e, my_goals: selectedGoals, my_intent_goal: null, my_intent_summary: null }
            : e,
        ),
      );
      setEditingId(null);
      toast.success('Updated. Your matches for this event will refresh shortly.');
    } catch {
      toast.error('Could not save goals, please try again.');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className='bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6'>
      <h2 className='text-base font-semibold text-heading-500 mb-1'>My events</h2>
      <p className='text-sm text-body-400 mb-4'>
        What you are looking for at each event. This steers your matches for that
        event only; your profile stays the same everywhere.
      </p>
      <div className='space-y-3'>
        {events.map((ev) => {
          const goals = ev.my_goals ?? [];
          const isEditing = editingId === ev.id;
          return (
            <div key={ev.id} className='border border-gray-100 rounded-xl p-3'>
              <div className='flex items-start justify-between gap-3'>
                <div className='min-w-0'>
                  <div className='flex items-center gap-2'>
                    <Calendar className='w-4 h-4 text-primary-500 flex-shrink-0' />
                    <span className='font-medium text-heading-500 truncate'>{ev.name}</span>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                        ev.status === 'ended'
                          ? 'bg-gray-100 text-gray-500'
                          : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {ev.status}
                    </span>
                  </div>
                  {!isEditing && (
                    <>
                      {/* Intent first: the interview supersedes the chips and
                          clears my_goals, so checking goals alone would report
                          "same as your profile" to someone who just told us the
                          opposite. */}
                      <p className='text-sm text-body-500 mt-1 ml-6'>
                        {ev.my_intent_goal
                          ? ev.my_intent_goal
                          : goals.length > 0
                            ? goals.map(goalLabel).join(', ')
                            : 'Same as your profile objective'}
                      </p>
                      {ev.my_intent_goal && ev.my_intent_summary && (
                        <p className='text-xs text-body-400 mt-0.5 ml-6'>{ev.my_intent_summary}</p>
                      )}
                    </>
                  )}
                </div>
                {!isEditing && ev.status !== 'ended' && (
                  <button
                    onClick={() => startEdit(ev)}
                    className='text-sm text-primary-600 underline flex-shrink-0'
                  >
                    Edit
                  </button>
                )}
              </div>

              {isEditing && (
                <div className='mt-3 ml-6 space-y-1.5'>
                  {VALID_GOALS.map((goal) => (
                    <button
                      key={goal.value}
                      onClick={() => toggleGoal(goal.value)}
                      className={`w-full text-left px-3 py-2 rounded-lg border text-xs transition-colors ${
                        selectedGoals.includes(goal.value)
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300 text-body-400 bg-white'
                      }`}
                    >
                      {selectedGoals.includes(goal.value) && (
                        <Check className='w-3.5 h-3.5 inline mr-1.5' />
                      )}
                      {goal.label}
                    </button>
                  ))}
                  <p className='text-[11px] text-body-300'>
                    Pick up to 2, or clear all to use your profile objective.
                  </p>
                  <div className='flex gap-2 pt-1'>
                    <button
                      onClick={() => save(ev)}
                      disabled={savingId === ev.id}
                      className='px-3 py-1.5 rounded-lg bg-primary-500 text-white text-xs font-semibold disabled:opacity-50'
                    >
                      {savingId === ev.id ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className='px-3 py-1.5 rounded-lg border border-gray-200 text-xs'
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MyEventsCard;
